<template>
    <div id="app">
        <TabOnlyFocus/>
        <Header-banner/>
        <div
            class="masthead"
            :class="{
                'masthead--error-enabled': blockingError.enabled,
            }"
        >
            <h1 class="masthead__headline headline6">
                Configure Lightspeed eCom
                <span class="masthead__headline-error" v-if="blockingError.enabled">(Error)</span>
            </h1>
            <div
                class="masthead__controls"
                :class="{
                    'masthead__controls--loading': submitting,
                }"
            >
                <div class="masthead__controls_content">
                    <Dropdown
                        class="dropdown"
                        placeholder="Language"
                        :options="languages"
                        :value="currentLanguage"
                        v-if="!blockingError.enabled && languages.length > 1"
                        v-model="languageDropdownValue"
                    />
                    <div class="save-button" v-if="currentLanguage">
                        <Button
                            :actionText="`Save '${currentLanguage.displayValue}'`"
                            @click.native="save"
                        />
                    </div>
                </div>
                <div class="masthead__controls_loader">
                    Submitting
                    <LoaderCircular class="masthead__controls_loader_icon"/>
                </div>
            </div>
        </div>
        <main class="loading" v-if="shopSettingsLoading">
            <LoaderCircular/>
        </main>
        <main class="main" v-else-if="currentLanguage">
            <router-view/>
        </main>
        <main class="language-enforcer" v-else-if="!blockingError.enabled">
            <DropdownQuestion
                v-if="languages.length > 1"
                class="dropdown"
                placeholder="Language"
                :options="languages"
                :required="true"
                help-text="All your different languages need to be configured separately"
                label="Please pick a language before you continue"
                v-model="languageDropdownValue"
            />
        </main>
        <main class="error" v-else>
            {{blockingError.message}}
            <div class="error__message">
                {{blockingError.error}}
            </div>
        </main>
        <NotificationRail :notificationList="notificationList"/>
    </div>
</template>

<script>
import {
    NotificationRail,
    Dropdown,
    DropdownQuestion,
    Button,
    TabOnlyFocus,
    LoaderCircular,
} from 'feedback-company-vue-components';

import HeaderBanner from '@/components/molecules/HeaderBanner.vue';

export default {
    name: 'app',
    components: {
        NotificationRail,
        HeaderBanner,
        Dropdown,
        DropdownQuestion,
        Button,
        TabOnlyFocus,
        LoaderCircular,
    },
    data() {
        return {
            languageDropdownValue: '',
        };
    },
    computed: {
        notificationList() {
            return this.$store.state.notifications.all;
        },
        languages() {
            return this.$store.state.languages;
        },
        currentLanguage() {
            return this.$store.getters.currentLanguage;
        },
        shopSettingsLoading() {
            return this.$store.state.shopSettingsLoading;
        },
        blockingError() {
            return this.$store.state.blockingError;
        },
        submitting() {
            return this.$store.state.shopSettingsSubmitting;
        },
    },
    watch: {
        currentLanguage() {
            this.$store.dispatch('getShop');
        },
        languageDropdownValue(language) {
            this.$store.commit('setSelectedLanguage', language);
        },
    },
    methods: {
        save() {
            this.$store.dispatch('saveSettings');
        },
    },
};
</script>

<style lang="scss">
    @import "./src/style_variables/style_variables";
    @import "~feedback-company-vue-components/src/style_variables/typography_classes";

    html, body {
        padding: 0px;
        margin: 0px;
        background-color: $grey_bg;
    }
    .main {
        position: relative;
        width: 100%;
    }
    .language-enforcer, .error, .loading {
        @extend .body1;
        max-width: 792px;
        margin: 24px auto;
        padding: 0px 16px;
    }
    .loading {
        display: flex;
        justify-content: center;
    }
    .error__message {
        @extend .body2;
        margin-top: 16px;
        color: $red;
    }
    .masthead {
        width: 100%;
        max-width: 792px;
        height: 72px;
        background-color: white;
        z-index: 3;
        border-bottom: 1px solid $grey06;
        border-top-left-radius: 14px;
        border-top-right-radius: 14px;
        position: sticky;
        top: 0px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0px 8px;
        @media (max-width: 498px) {
            justify-content: flex-end;
        }
        @media (max-width: 769px) {
            margin: -24px auto 0px auto;
        }
        @media (min-width: 768px) {
            margin: -72px auto 0px auto;
        }
        &.masthead--error-enabled {
            justify-content: flex-start !important;
            .masthead__headline {
                display: inline !important;
            }
        }
    }
    .masthead__headline {
        margin: 0px;
        padding-left: 8px;
        @media (max-width: 660px) {
            font-size: 3.1vw;
        }
        @media (max-width: 542px) {
            font-size: 16px;
        }
        @media (max-width: 498px) {
            display: none;
        }
    }
    .masthead__headline-error {
        color: $red;
    }
    .masthead__controls {
        display: flex;
        height: 56px;
        flex-direction: column;
        position: relative;
        &.masthead__controls--loading {
            .masthead__controls_content {
                opacity: 0;
            }
            .masthead__controls_loader {
                opacity: 1;
            }
        }
    }
    .masthead__controls_content,
    .masthead__controls_loader {
        transition: opacity 0.4s;
    }
    .masthead__controls_content {
        display: flex;
        height: 56px;
        opacity: 1;
        position: relative;
        z-index: 2;
    }
    .masthead__controls_loader {
        @extend .body2;
        position: absolute;
        height: 56px;
        opacity: 0;
        margin: 0;
        display: flex;
        align-items: center;
        right: 0px;
        padding-right: 12px;
    }
    .masthead__controls_loader_icon {
        margin-left: 12px;
    }

    .dropdown {
        display: inline;
    }
    .save-button {
        display: inline;
        margin-left: 8px;
        .button__button {
            height: 56PX;
        }
    }

</style>
